// ─────────────────────────────────────────────────────────────────────────────
// "MY BROKER ISN'T ON DOORLYPRO" — AND THE MEMBER CAN NOW FIX IT THEMSELVES.
//
// 🚨 WHY THIS EXISTS. Both the agent and the broker sign. An agent whose broker
// is not registered has an agreement that can never be counter-signed, and a firm
// with no payee contact that therefore cannot be paid — blocked on two fronts by
// somebody else's absence, with no lever from their own portal.
//
// 🔑 IT WAS A NOMINATION AND IT IS NOW AN INVITE. The first version routed every
// case to a Doorly rep, reasoning that broker authority is too much for a junior
// member to hand out. The objection that overturned it: that holds a partner's own
// pay behind a queue they cannot see, staffed by people whose absence they cannot
// do anything about. The gate moved rather than disappearing — see the three
// outcomes below — and what replaces it is visibility: the firm and its Partner
// Success owner are told, by email, the moment a broker seat is filled.
//
// ⚠️ THREE OUTCOMES, NOT TWO, and the middle one is the whole point:
//   • the state's record NAMES this person as the firm's designated broker → sent
//   • no record exists to check (every mortgage originator, since NMLS Consumer
//     Access forbids the lookup) → sent, on the member's attestation
//   • a record exists and says OTHERWISE → held for a person
// "Send unless the state disagrees" and "send only if the state agrees" differ
// only in the case that matters, which is the originator with nothing to check.
//
// ⚠️ IT RENDERS ONLY WHEN IT IS TRUE. The server answers `needed` — the member has
// a firm, that firm has no broker, and they are not the account holder themselves.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const input = { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

// 🚨 `openSignal` IS HOW THE TASK BUTTONS REACH THIS CARD. The banking and
// counter-signature tasks offer "Invite broker" and used to navigate to
// /doorlypro/dashboard — the page the partner is already on — so the button did
// nothing at all, twice, on the screen that tells them their pay is held.
//
// ⚠️ A COUNTER, NOT A BOOLEAN. A second click on the same button has to reopen
// and re-scroll; a boolean that is already true fires no effect.
export default function BrokerNomination({ token, openSignal = 0 }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ broker_name: '', broker_email: '', broker_phone: '', broker_license: '', note: '' });
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/me/broker-nomination`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, [token]);
  useEffect(load, [load]);

  // ⚠️ NO SCROLL ANY MORE. The form used to expand inline under the task list, so
  // opening it changed nothing a partner could see on a short screen and the
  // button read as dead — the same complaint, one layer down. It is a dialog now:
  // fixed position, centred, visible wherever the page happens to be.
  useEffect(() => {
    if (!openSignal) return;
    setOpen(true);
  }, [openSignal]);

  // ── 🚨 registry / licensePlaceholder / submit ARE DEFINED BEFORE THE EARLY
  //        RETURNS, because the dialog below is rendered from every state ──────
  //
  // ⚠️ `state` CAN BE null HERE. These used to sit after `if (!state ...) return
  // null`, so they could read state freely; now they cannot. The optional reads
  // are deliberate and the registry simply has no answer until the fetch lands,
  // which means no license field — the same thing it means for a homebuilder.
  // 🚨 null IS A REAL ANSWER — A HOMEBUILDER HOLDS NEITHER LICENSE. This was
  // `state.licenseRegistry || 'TREC'`, so a missing registry fell through to
  // asking for a TREC number, which is the same fall-through the server side of
  // this just stopped doing. No registry means no license field at all.
  // 🚨 OPTIONAL. This read `state.licenseRegistry` and sat BELOW
  // `if (!state) return null`, so state was guaranteed. It is not any more —
  // the dialog is built before that guard, and on the first render, before the
  // fetch lands, this line would have thrown and taken the dashboard with it.
  const registry = (state && state.licenseRegistry) || null;
  const licensePlaceholder = registry === 'NMLS'
    ? 'Broker’s NMLS ID' : 'Broker’s TREC license number';

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/doorlypro/me/broker-nomination`,
        { ...form, attest },
        { headers: { Authorization: `Bearer ${token}` } });
      // ⚠️ THE TWO OUTCOMES READ DIFFERENTLY, because they ARE different. One is
      // done; the other is with a person. Saying "thanks" to both leaves somebody
      // waiting on an email that already went out.
      setResult(r.data && r.data.sent
        ? { sent: true, name: r.data.designatedBroker, basis: r.data.basis }
        : { sent: false });
      setOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not send that.');
    } finally { setBusy(false); }
  };
  // ── 🚨 THE DIALOG IS A SIBLING OF THE CARD, NOT A CHILD OF IT ──────────────
  //
  // It used to live INSIDE the "your firm has no broker" card, after four early
  // returns. So `openSignal` set `open` to true on a component that had already
  // returned null or a notice, and the dashboard's "Invite broker" button set a
  // flag on markup that was never rendered. Three live ways to reach that:
  //
  //   · the dashboard mounted this only for an ACTIVE partner, while the task
  //     list that CARRIES the button deliberately renders before activation —
  //     partner #59 sits in training_pending with a live button today;
  //   · a pending nomination (the held case) rendered a notice and returned,
  //     while the task row still said "Invite broker";
  //   · `needed` here and the task's own broker lookup were two separate
  //     queries, so the button could appear on one reading while the dialog's
  //     existence depended on the other.
  //
  // 🔑 SO WHATEVER THE CARD DECIDES TO BE — including nothing — the dialog is
  // reachable, and a button that opens it cannot be dead.
  //
  // ⚠️ IT STILL OPENS ONLY WHEN SOMETHING ASKS. Nothing renders it open by
  // itself; the only two things that ask are the card's own button and the
  // dashboard task CTA, which the server sets to null once a broker exists.
  // 🔑 A DIALOG, NOT AN INLINE EXPANSION. The form grew to eight controls — four
  // fields, an attestation, two buttons and a paragraph explaining what will be
  // checked — and unfolding that inside a notice card pushed the whole dashboard
  // down, so the fields a partner had just been asked to fill appeared below the
  // fold on the page they were already reading.
  //
  // ⚠️ THE OVERLAY CLOSES ON A BACKDROP CLICK AND THE PANEL DOES NOT.
  // stopPropagation on the inner div — without it, a click on any field bubbles
  // to the backdrop and discards a half-typed invitation.
  const dialog = open ? (
          <div onClick={() => { setOpen(false); setError(''); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460,
              maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <h3 style={{ margin: '0 0 3px', fontSize: 17, fontWeight: 800, color: '#111827' }}>Invite your broker</h3>
                <p style={{ margin: 0, fontSize: 12.5, color: '#6b7280' }}>
                  They counter-sign your agreement and set up your firm’s payout details.
                </p>
              </div>
              <button onClick={() => { setOpen(false); setError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1 }}>✕</button>
            </div>
            <input style={input} placeholder="Broker's full name" value={form.broker_name}
              onChange={(e) => setForm((p) => ({ ...p, broker_name: e.target.value }))} />
            <input style={input} type="email" placeholder="Broker's email" value={form.broker_email}
              onChange={(e) => setForm((p) => ({ ...p, broker_email: e.target.value }))} />
            <input style={input} placeholder="Broker's phone (optional)" value={form.broker_phone}
              onChange={(e) => setForm((p) => ({ ...p, broker_phone: e.target.value }))} />
            {/* 🔑 THE FIELD IS LABELLED BY REGISTRY, NOT BY GUESS. A realtor holds a
                TREC license and an originator an NMLS ID; one caption for both asks
                half the partners for a number they do not have. The server supplies
                which, from the member's own partner type. */}
            {registry && (
              <input style={input} placeholder={licensePlaceholder} value={form.broker_license}
                onChange={(e) => setForm((p) => ({ ...p, broker_license: e.target.value }))} />
            )}
            <input style={input} placeholder="Anything we should know (optional)" value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />

            {/* 🚨 THE ATTESTATION IS WHAT AN UNVERIFIED SEND COSTS. It is asked of
                everybody rather than only of originators, because the form cannot
                know before submitting whether the license will check out — and a
                round trip that comes back demanding one more tick is worse than a
                tick nobody needed. The server is the gate; this is the statement. */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#4b5563', lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={attest} style={{ marginTop: 2 }}
                onChange={(e) => setAttest(e.target.checked)} />
              <span>
                I confirm this person is my firm’s broker of record, and I understand they’ll be able
                to see every buyer and loan file my firm has referred and to manage our payout details.
              </span>
            </label>

            {error && <div style={{ fontSize: 13, color: '#ff082f' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={submit} disabled={busy || !attest}
                style={{ padding: '9px 16px', background: attest ? '#3a6ad9' : '#c7d2e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: attest ? 'pointer' : 'not-allowed' }}>
                {busy ? 'Sending…' : 'Send invitation'}
              </button>
              <button onClick={() => { setOpen(false); setError(''); }}
                style={{ padding: '9px 12px', background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
            {/* 🚨 WHAT ACTUALLY DECIDES IT, SAID BEFORE THEY SUBMIT — including the
                case where nothing can be checked, which must not read as a refusal. */}
            <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>
              {!registry
                ? <>There’s no license for us to check here, so the invitation goes out on your
                  confirmation above. Your colleagues and your Doorly rep are told when it does.</>
                : state && state.licenseCheckable
                ? <>We’ll check the license number against the state’s record of your firm’s broker.
                  If it matches, the invitation goes out immediately. If the record says someone else
                  is your broker, we’ll confirm with you first.</>
                : <>There’s no public record of mortgage company structure we’re permitted to check,
                  so the invitation goes out on your confirmation above. Your colleagues and your
                  Doorly rep are told when it does.</>}
            </div>
          </div>
          </div>
  ) : null;
  // ── the card: the amber prompt, a notice, or nothing at all ────────────────
  const card = (() => {
    // ⚠️ THE CONFIRMATION SURVIVES THE RELOAD that follows a send. `needed` goes
    // false the moment the invite exists, so without this the member's screen
    // would simply go blank and they would not know it worked.
    if (result && result.sent) {
      return (
        <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 13, color: '#166534' }}>
          <strong>Invitation sent.</strong>{' '}
          {/* ⚠️ THE TWO BASES ARE NOT DESCRIBED THE SAME WAY. Telling somebody their
              claim was "confirmed" when nothing checked it is the one sentence that
              would make this design dishonest rather than merely fast. */}
          {result.basis === 'trec_designated_broker'
            ? <>The state license record names{result.name ? ` ${result.name}` : ' them'} as your firm’s
              broker of record, so we invited them straight away.</>
            : <>We’ve invited them on your confirmation that they’re your firm’s broker of record —
              there’s no public record we’re able to check that against. Your colleagues and your
              Doorly rep have been told, so anything that looks wrong can be put right quickly.</>}
          {' '}They’ll be able to counter-sign your agreement and set up your firm’s payout details
          once they register — which is what releases any partner fees you’ve earned.
        </div>
      );
    }

    if (!state || !state.needed) return null;

    // ⚠️ THIS IS NOW THE HELD CASE ONLY — the state contradicted the license given.
    // Every case that merely could not be checked was invited instead and never
    // reaches here.
    if (state.nomination) {
      return (
        <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f5f8ff', border: '1px solid #c5d3f5', borderRadius: 10, fontSize: 13, color: '#374151' }}>
          <strong>We’re checking your broker’s details.</strong> You told us about{' '}
          {state.nomination.broker_name} ({state.nomination.broker_email}), and the license record
          didn’t line up the way we expected — so a person at Doorly is confirming it rather than
          guessing. Nothing else is needed from you, and your buyers are not held up by it.
        </div>
      );
    }



    return (
      <div style={{ marginBottom: 20, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          Your firm doesn’t have a broker on DoorlyPro yet
        </div>
        {/* ⚠️ THE CONSEQUENCE IS STATED PLAINLY AND SO IS ITS LIMIT. An agent should
            know their agreement and their firm's payout details are waiting on
            somebody — and should NOT think their work is frozen, because it is not. */}
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
          Your agreement needs your broker’s signature as well as yours, and Doorly pays your firm
          rather than you — so your broker is also who sets up where that money goes. You can invite
          them yourself, right here.
          <br />
          {/* 🚨 THIS SAID "nothing of yours is on hold", AND THAT WENT FALSE the day
              the counter-signature started gating payout. The reassurance that
              still holds is about the WORK; the fees are genuinely held, and a
              partner who learns that from a missing payment rather than from here
              has been misled by their own dashboard. Held, not cancelled — both
              halves, or it reads as a penalty. */}
          <span style={{ color: '#6b7280' }}>
            You can keep working and submitting buyers in the meantime — none of that is affected.
            Partner fees you earn are held until your broker counter-signs; they are not cancelled,
            and they are released as soon as that happens.
          </span>
        </p>

        <button onClick={() => setOpen(true)}
          style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Invite my broker
        </button>
      </div>
    );
  })();

  return <>{card}{dialog}</>;
}
